import { gsap } from 'gsap';
import { chooseLabelPlacement, getMapMobileFocus } from './map-layout.utils.js';
import MetaMap from '../../lib/meta-map.js';

const initializedSections = new WeakMap();
const mobileBreakpoint = 640;
const mobileInset = 10;
const maximumMobileFitPasses = 8;

const initFrameZoom = (section, map) => {
  const stage = section.querySelector('[data-map-stage]');
  const viewport = section.querySelector('[data-map-viewport]');
  const pins = [...section.querySelectorAll('.map-sec__pin')];
  if (!stage || !viewport) return;

  gsap.set(stage, { transformOrigin: '0 0' });

  let activeCode = null;
  const zoomScale = 1.5;

  const syncActiveCountry = () => {
    if (activeCode) section.dataset.mapActiveCountry = activeCode;
    else delete section.dataset.mapActiveCountry;
    pins.forEach((pin) => {
      const active = pin.dataset.countryCode === activeCode;
      pin.hidden = Boolean(activeCode) && !active;
      pin.inert = pin.hidden;
      const label = pin.querySelector('.map-sec__pin-label');
      label?.classList.toggle('is-active', active);
      label?.setAttribute('aria-pressed', String(active));
    });
    section.querySelectorAll('.meta-map__country').forEach((country) => {
      country.classList.toggle('is-active', country.dataset.countryCode === activeCode);
    });
  };

  const animateTo = (scale, x, y) => {
    gsap.to(stage, {
      scale,
      x,
      y,
      duration: 0.7,
      ease: 'power2.out',
      overwrite: 'auto',
    });
  };

  const resetZoom = () => {
    if (!activeCode) return;
    activeCode = null;
    syncActiveCountry();
    animateTo(1, 0, 0);
  };

  const zoomToCountry = (code) => {
    if (!code) return;
    if (activeCode === code) {
      resetZoom();
      return;
    }

    const pin = pins.find((p) => p.dataset.countryCode === code);
    let xPct = 50;
    let yPct = 50;

    if (pin) {
      const matchX = pin.style.getPropertyValue('--pin-x').match(/[\d.]+/);
      const matchY = pin.style.getPropertyValue('--pin-y').match(/[\d.]+/);
      if (matchX) xPct = parseFloat(matchX[0]);
      if (matchY) yPct = parseFloat(matchY[0]);
    } else if (map && typeof map.countryAnchor === 'function') {
      const anchor = map.countryAnchor(code);
      if (anchor) {
        xPct = anchor.x_percent;
        yPct = anchor.y_percent;
      }
    }

    activeCode = code;
    syncActiveCountry();

    const W = stage.clientWidth;
    const H = stage.clientHeight;
    const Vw = viewport.clientWidth;
    const Vh = viewport.clientHeight;

    const Px = (xPct / 100) * W;
    const Py = (yPct / 100) * H;

    let Tx = Vw / 2 - Px * zoomScale;
    let Ty = Vh / 2 - Py * zoomScale;

    const minTx = Math.min(0, Vw - W * zoomScale);
    const maxTx = Math.max(0, Vw - W * zoomScale);
    Tx = Math.min(maxTx, Math.max(minTx, Tx));

    const minTy = Math.min(0, Vh - H * zoomScale);
    const maxTy = Math.max(0, Vh - H * zoomScale);
    Ty = Math.min(maxTy, Math.max(minTy, Ty));

    animateTo(zoomScale, Tx, Ty);
  };

  pins.forEach((pin) => {
    const label = pin.querySelector('.map-sec__pin-label');
    if (!label) return;
    label.addEventListener('click', (event) => {
      event.stopPropagation();
      zoomToCountry(pin.dataset.countryCode);
    });
  });

  stage.addEventListener('click', (event) => {
    const country = event.target.closest('.meta-map__country.is-selected');
    if (country) {
      event.stopPropagation();
      zoomToCountry(country.dataset.countryCode);
      return;
    }
    if (event.target.closest('.map-sec__pin-label')) return;
    resetZoom();
  });

  document.addEventListener('click', (event) => {
    if (!activeCode) return;
    if (section.contains(event.target)) {
      return;
    }
    resetZoom();
  });

  window.addEventListener('resize', () => {
    if (activeCode) {
      resetZoom();
    }
  });
  section.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') resetZoom();
  });
  syncActiveCountry();
};

const getSections = (root) => {
  const sections = [];

  if (root instanceof Element && root.matches('[data-map-section]')) {
    sections.push(root);
  }

  if (root?.querySelectorAll) {
    sections.push(...root.querySelectorAll('[data-map-section]'));
  }

  return sections;
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
};

const initializeSection = (section) => {
  if (initializedSections.has(section)) {
    initializedSections.get(section).schedule();
    return;
  }

  const viewport = section.querySelector('[data-map-viewport]');
  const stage = section.querySelector('[data-map-stage]');
  if (!viewport || !stage) {
    return;
  }

  if (section.dataset.mapMode === 'frame') {
    const frameMap = section.querySelector('[data-map-frame]');
    if (frameMap) {
      initializedSections.set(section, { schedule: () => {} });
      let selectedCountries = [];
      try {
        selectedCountries = JSON.parse(section.dataset.mapCountries || '[]');
      } catch {
        selectedCountries = [];
      }
      const map = new MetaMap(frameMap, {
        mapPath: section.dataset.mapPath,
        selectedCountries,
      });
      map.ready.then(() => {
        initFrameZoom(section, map);
      }).catch(() => {
        frameMap.textContent = 'The world map could not be loaded.';
      });
    }
    return;
  }

  const pins = [...section.querySelectorAll('[data-map-pin]')].filter((pin) => (
    pin.querySelector('[data-map-label]')
  ));

  if (pins.length === 0) {
    return;
  }

  let frame = 0;
  let shouldFitMobile = true;
  let mobileViewportWidth = 0;

  const isMobileOptimized = () => (
    section.hasAttribute('data-map-mobile-optimized')
    && window.innerWidth <= mobileBreakpoint
  );

  const getLabelRects = () => pins.map((pin) => {
    const label = pin.querySelector('[data-map-label]');
    const left = Number.parseFloat(label.style.getPropertyValue('--map-label-left'));
    const top = Number.parseFloat(label.style.getPropertyValue('--map-label-top'));

    return {
      left: pin.offsetLeft + (Number.isFinite(left) ? left : 0),
      top: pin.offsetTop + (Number.isFinite(top) ? top : 0),
      width: label.offsetWidth,
      height: label.offsetHeight,
    };
  });

  const getMarkerRects = () => pins.map((pin) => {
    const marker = pin.querySelector('.map-sec__marker');
    const size = marker?.offsetWidth || 0;

    if (!size) {
      return null;
    }

    return {
      left: pin.offsetLeft - (size / 2),
      top: pin.offsetTop - (size / 2),
      width: size,
      height: size,
    };
  }).filter(Boolean);

  const placeLabels = () => {
    const occupied = [];
    const stageBounds = {
      left: 0,
      top: 0,
      width: stage.clientWidth,
      height: stage.clientHeight,
    };
    const viewportBounds = {
      left: viewport.scrollLeft,
      top: 0,
      width: viewport.clientWidth,
      height: viewport.clientHeight,
    };

    pins.forEach((pin) => {
      const label = pin.querySelector('[data-map-label]');
      const marker = pin.querySelector('.map-sec__marker');
      const placement = chooseLabelPlacement({
        pin: {
          x: pin.offsetLeft,
          y: pin.offsetTop,
          size: marker?.offsetWidth || 13,
        },
        label: { width: label.offsetWidth, height: label.offsetHeight },
        stage: stageBounds,
        viewport: viewportBounds,
        preferred: {
          side: pin.dataset.preferredSide,
          align: pin.dataset.labelAlign,
          offsetX: toNumber(pin.dataset.offsetX),
          offsetY: toNumber(pin.dataset.offsetY),
        },
        occupied,
      });

      label.style.setProperty('--map-label-left', `${placement.left - pin.offsetLeft}px`);
      label.style.setProperty('--map-label-top', `${placement.top - pin.offsetTop}px`);
      pin.dataset.resolvedSide = placement.side;
      occupied.push(placement);
    });

    section.classList.add('is-labels-ready');
  };

  const fitMobileStage = () => {
    if (!isMobileOptimized()) {
      stage.style.removeProperty('--map-mobile-stage-width');
      mobileViewportWidth = 0;
      return;
    }

    if (mobileViewportWidth === viewport.clientWidth) {
      return;
    }

    mobileViewportWidth = viewport.clientWidth;
    stage.style.removeProperty('--map-mobile-stage-width');
    placeLabels();

    const availableWidth = Math.max(viewport.clientWidth - (mobileInset * 2), 1);

    for (let pass = 0; pass < maximumMobileFitPasses; pass += 1) {
      const focus = getMapMobileFocus({
        labels: getLabelRects(),
        markers: getMarkerRects(),
        viewportWidth: viewport.clientWidth,
        stageWidth: stage.clientWidth,
        inset: mobileInset,
      });

      if (focus.width <= availableWidth || stage.clientWidth <= viewport.clientWidth) {
        break;
      }

      const nextWidth = Math.max(
        viewport.clientWidth,
        Math.floor(stage.clientWidth * (availableWidth / focus.width)),
      );

      if (nextWidth >= stage.clientWidth) {
        break;
      }

      stage.style.setProperty('--map-mobile-stage-width', `${nextWidth}px`);
      placeLabels();
    }
  };

  const layout = () => {
    frame = 0;

    if (!stage.clientWidth || !stage.clientHeight) {
      return;
    }

    if (shouldFitMobile) {
      fitMobileStage();
    }

    placeLabels();

    if (shouldFitMobile && isMobileOptimized()) {
      const focus = getMapMobileFocus({
        labels: getLabelRects(),
        markers: getMarkerRects(),
        viewportWidth: viewport.clientWidth,
        stageWidth: viewport.scrollWidth,
        inset: mobileInset,
      });

      if (Math.abs(viewport.scrollLeft - focus.scrollLeft) > 1) {
        viewport.scrollLeft = focus.scrollLeft;
        placeLabels();
      }
    }

    shouldFitMobile = false;
  };

  const schedule = (fitMobile = false) => {
    shouldFitMobile = shouldFitMobile || fitMobile;

    if (frame) {
      return;
    }

    frame = window.requestAnimationFrame(layout);
  };

  viewport.addEventListener('scroll', () => schedule(), { passive: true });

  const image = stage.querySelector('img');
  if (image && !image.complete) {
    image.addEventListener('load', () => schedule(true), { once: true });
  }

  const resizeObserver = new ResizeObserver(() => schedule(true));
  resizeObserver.observe(viewport);
  resizeObserver.observe(stage);

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => schedule(true));
  }

  initializedSections.set(section, { schedule, resizeObserver });
  schedule(true);
};

const initMapSections = (root = document) => {
  getSections(root).forEach(initializeSection);
};

window.initMapSections = initMapSections;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initMapSections(), { once: true });
} else {
  initMapSections();
}

export { initMapSections };
